// =====================================================
// Swiss Finance OS - Betroffenenrechte Interface
// DSGVO Art. 15-22 / FADP Art. 12 konforme Umsetzung
// =====================================================

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Download, 
  Trash2, 
  Edit, 
  Eye, 
  FileText, 
  Shield, 
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';

interface DataSubjectRequest {
  id: string;
  type: 'access' | 'rectification' | 'erasure' | 'portability' | 'restriction' | 'objection';
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  submittedAt: string;
  description: string;
  response?: string;
  completedAt?: string;
}

export const DataSubjectRights = () => {
  const [selectedRight, setSelectedRight] = useState<string>('');
  const [requestDescription, setRequestDescription] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRequests, setSubmittedRequests] = useState<DataSubjectRequest[]>([]);

  const rights = [
    {
      id: 'access',
      title: 'Auskunftsrecht',
      description: 'Sie haben das Recht zu erfahren, welche personenbezogenen Daten wir über Sie verarbeiten.',
      icon: Eye,
      article: 'Art. 15 DSGVO'
    },
    {
      id: 'rectification',
      title: 'Berichtigungsrecht',
      description: 'Sie können die Berichtigung unrichtiger oder unvollständiger Daten verlangen.',
      icon: Edit,
      article: 'Art. 16 DSGVO'
    },
    {
      id: 'erasure',
      title: 'Löschungsrecht',
      description: 'Sie können die Löschung Ihrer personenbezogenen Daten unter bestimmten Voraussetzungen verlangen.',
      icon: Trash2,
      article: 'Art. 17 DSGVO'
    },
    {
      id: 'portability',
      title: 'Datenübertragbarkeit',
      description: 'Sie können die Übertragung Ihrer Daten in einem strukturierten, maschinenlesbaren Format verlangen.',
      icon: Download,
      article: 'Art. 20 DSGVO'
    },
    {
      id: 'restriction',
      title: 'Einschränkungsrecht',
      description: 'Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen.',
      icon: Shield,
      article: 'Art. 18 DSGVO'
    },
    {
      id: 'objection',
      title: 'Widerspruchsrecht',
      description: 'Sie können der Verarbeitung Ihrer Daten aus Gründen Ihres besonderen Interesses widersprechen.',
      icon: AlertCircle,
      article: 'Art. 21 DSGVO'
    }
  ];

  const handleSubmitRequest = async () => {
    if (!selectedRight || !requestDescription || !userEmail) {
      alert('Bitte füllen Sie alle Felder aus.');
      return;
    }

    setIsSubmitting(true);

    // Simuliere API-Aufruf
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newRequest: DataSubjectRequest = {
      id: `req_${Date.now()}`,
      type: selectedRight as any,
      status: 'pending',
      submittedAt: new Date().toISOString(),
      description: requestDescription
    };

    setSubmittedRequests(prev => [newRequest, ...prev]);
    setSelectedRight('');
    setRequestDescription('');
    setUserEmail('');
    setIsSubmitting(false);

    // Hier würde normalerweise eine E-Mail an den Datenschutzbeauftragten gesendet werden
    console.log('Data subject request submitted:', newRequest);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'rejected':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Abgeschlossen';
      case 'in_progress':
        return 'In Bearbeitung';
      case 'rejected':
        return 'Abgelehnt';
      default:
        return 'Ausstehend';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ihre Datenschutzrechte
        </h1>
        <p className="text-gray-600">
          Als betroffene Person haben Sie verschiedene Rechte bezüglich Ihrer personenbezogenen Daten. 
          Wählen Sie unten das gewünschte Recht aus und stellen Sie Ihre Anfrage.
        </p>
      </div>

      {/* Informationshinweis */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          <strong>Wichtiger Hinweis:</strong> Wir bearbeiten Ihre Anfrage innerhalb von 30 Tagen. 
          Bei komplexen Anfragen kann sich die Bearbeitungszeit auf bis zu 60 Tage verlängern. 
          Wir werden Sie über Verlängerungen informieren.
        </AlertDescription>
      </Alert>

      {/* Rechte-Auswahl */}
      <Card>
        <CardHeader>
          <CardTitle>Wählen Sie Ihr gewünschtes Recht aus</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {rights.map((right) => {
              const Icon = right.icon;
              return (
                <div
                  key={right.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedRight === right.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedRight(right.id)}
                >
                  <div className="flex items-start space-x-3">
                    <Icon className="h-5 w-5 text-blue-600 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{right.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{right.description}</p>
                      <p className="text-xs text-blue-600 mt-2">{right.article}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {selectedRight && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">E-Mail-Adresse *</Label>
                <Input
                  id="email"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="ihre.email@beispiel.ch"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Beschreibung Ihrer Anfrage *</Label>
                <Textarea
                  id="description"
                  value={requestDescription}
                  onChange={(e) => setRequestDescription(e.target.value)}
                  placeholder="Beschreiben Sie Ihre Anfrage so detailliert wie möglich..."
                  className="mt-1"
                  rows={4}
                />
              </div>

              <Button 
                onClick={handleSubmitRequest}
                disabled={isSubmitting || !userEmail || !requestDescription}
                className="w-full"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Anfrage stellen'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meine Anfragen */}
      {submittedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Ihre Anfragen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {submittedRequests.map((request) => {
                const right = rights.find(r => r.id === request.type);
                return (
                  <div key={request.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(request.status)}
                        <span className="font-medium">{right?.title}</span>
                        <span className="text-sm text-gray-500">({right?.article})</span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(request.submittedAt).toLocaleDateString('de-CH')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{request.description}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm px-2 py-1 rounded ${
                        request.status === 'completed' ? 'bg-green-100 text-green-800' :
                        request.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getStatusText(request.status)}
                      </span>
                      {request.status === 'completed' && (
                        <Button size="sm" variant="outline">
                          <FileText className="h-4 w-4 mr-2" />
                          Antwort anzeigen
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kontaktinformationen */}
      <Card>
        <CardHeader>
          <CardTitle>Kontakt zum Datenschutzbeauftragten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Für Fragen zum Datenschutz oder zur Ausübung Ihrer Rechte können Sie sich 
              jederzeit an unseren Datenschutzbeauftragten wenden:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">E-Mail</h4>
                <p className="text-sm text-blue-600">datenschutz@togeco.ch</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Post</h4>
                <p className="text-sm">
                  Togeco Buchhaltung<br/>
                  Datenschutzbeauftragter<br/>
                  [Adresse einfügen]
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium mb-2">Beschwerderecht</h4>
              <p className="text-sm text-gray-600">
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über 
                die Verarbeitung Ihrer personenbezogenen Daten zu beschweren.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataSubjectRights;
